import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Profile {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  real_name: string | null;
  country: string | null;
  birth_date: string | null;
  username: string | null;
  created_at: string;
  updated_at: string;
}

interface UpdateProfileParams {
  display_name?: string;
  avatar_url?: string;
  real_name?: string;
  country?: string;
  birth_date?: string;
  username?: string;
}

export const useProfile = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    placeholderData: (prev) => prev,
  });


  const updateProfile = useMutation({
    mutationFn: async (params: UpdateProfileParams) => {
      if (!user?.id) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('profiles')
        .update(params)
        .eq('user_id', user.id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Profile;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['profile', user?.id], data);
    },
  });

  const uploadAvatar = async (file: File): Promise<string> => {
    if (!user?.id) throw new Error('Not authenticated');

    const ALLOWED_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const MAX_SIZE = 5 * 1024 * 1024; // 5 MB

    const fileExt = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(fileExt)) {
      throw new Error('Only JPG, PNG, GIF or WEBP images are allowed.');
    }
    if (file.type && !ALLOWED_MIME.includes(file.type)) {
      throw new Error('Invalid image type.');
    }
    if (file.size > MAX_SIZE) {
      throw new Error('Image must be smaller than 5 MB.');
    }

    const fileName = `${user.id}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(fileName, file, {
        upsert: true,
        contentType: file.type || `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`,
      });

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('avatars')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  return {
    profile,
    isLoading,
    updateProfile,
    uploadAvatar,
  };
};
